import React, { useState } from 'react'
import { Typography, Spin, Table, Card, Tabs, Button } from 'antd'
import ReadingsFilter from './ReadingsFilter'
import useDevices from '../hooks/useDevices'
import useElements from '../hooks/useElements'
import ChartPanel from './ChartPanel'
import { apiFetch } from "../utils/api";
import { formatBrowser } from '../utils/dateUtils';
import { useGlobalLoading } from '../context/LoadingContext';

const { Title, Paragraph } = Typography

export default function ReadingsView() {
  const { devices, loading: loadingDevices } = useDevices()
  const { elements, loading: loadingElements } = useElements()
  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedElements, setSelectedElements] = useState([])
  const [dateRange, setDateRange] = useState(null)
  const [readings, setReadings] = useState([])
  // const [loading, setLoading] = useState(false)
  const [element, setElement] = useState(null)
  const [periodicity, setPeriodicity] = useState('AVG5M')
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { startLoading, stopLoading } = useGlobalLoading();


  const handleApplyFilters = async (deviceIds, elementIds, range) => {
    setPage(1);
    setSelectedDevices(deviceIds);
    setSelectedElements(elementIds);
    setDateRange(range);

    if (!deviceIds.length || !elementIds.length || !range) return;

    try {
      startLoading();
      const res = await apiFetch('/api/readings/filter', {
        method: 'POST',
        body: JSON.stringify({
          deviceIds,
          from: range.from.toISOString(),
          to: range.to.toISOString(),
          elementIds,
          periodicity
        }),
      });

      if (!res?.ok) throw new Error(`HTTP ${res?.status}`);

      const data = await res.json();
      setReadings(data);

    } catch (err) {
      console.error('Failed to load readings', err);
      setReadings([]);
    } finally {
      stopLoading();
    }
  };


  function escapeCSVField(field) {
    if (field === null || field === undefined) return '';
    const str = String(field);
    if (str.includes('"')) {
      // Escapa comillas dobles con doble comilla
      return `"${str.replace(/"/g, '""')}"`;
    }
    if (str.includes(',') || str.includes('\n')) {
      // Campo con coma o salto de línea, envolver entre comillas dobles
      return `"${str}"`;
    }
    return str;
  }

  const exportToCSV = () => {
    const headers = ['Device', 'Element', 'Value', 'Read Date'];
    const rows = readings.map(r => [
      r.deviceName,
      r.elementName,
      r.readValue,
      formatBrowser(r.readDate, { seconds: true })
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(escapeCSVField).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'readings.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const seriesByDeviceElement = {};
  readings.forEach(r => {
    const key = `${r.deviceName} / ${r.elementName}`;
    if (!seriesByDeviceElement[key]) {
      seriesByDeviceElement[key] = [];
    }
    seriesByDeviceElement[key].push({
      date: r.readDate,
      value: r.readValue,
      unit: r.unit
    });
  });
  Object.values(seriesByDeviceElement).forEach(arr => arr.sort((a, b) => new Date(a.date) - new Date(b.date)));
  const series = Object.entries(seriesByDeviceElement).map(([key, arr]) => ({
    name: key,
    data: arr.map(d => [new Date(d.date).getTime(), d.value]),
    unit: arr[0]?.unit || ''
  }));

  const tabsItems = [
    {
      key: 'chart',
      label: 'Chart',
      children: (
        <div style={{ height: 550, width: '100%' }}>
          <ChartPanel
            title="Sensor Readings"
            series={series}
            options={{
              chart: { height: 550 }
            }}
          />
        </div>
      )
    },
    {
      key: 'table',
      label: 'Table',
      children: (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <Button onClick={exportToCSV} type="primary">Export CSV</Button>
          </div>
          <Card style={{ marginBottom: 24 }}>
            <Table
              dataSource={readings.map((r, i) => ({ ...r, key: i }))}
              columns={[
                { title: 'Device', dataIndex: 'deviceName', key: 'deviceId' },
                { title: 'Element', dataIndex: 'elementName', key: 'elementId' },
                { title: 'Value', dataIndex: 'readValue', key: 'readValue' },
                // { title: 'Validation', dataIndex: 'validationFlag', key: 'validationFlag' },
                {
                  title: 'Read Date',
                  dataIndex: 'readDate',
                  key: 'readDate',
                  render: text => formatBrowser(text, { seconds: true })
                }
              ]}
              size="small"
              pagination={{
                current: page,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (p) => setPage(p),
                onShowSizeChange: (_, size) => {
                  setPageSize(size);
                  setPage(1);
                }
              }}
            />
          </Card>
        </div>
      )
    }
  ]

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <Title level={3}>Historical</Title>

      <ReadingsFilter
        devices={devices}
        initialDevices={selectedDevices}
        initialRange={dateRange}
        onApplyFilters={handleApplyFilters}
        disabledDevices={loadingDevices}
        elements={elements}
        initialElements={selectedElements}
        disabledElements={loadingElements}
        periodicity={periodicity}
        setPeriodicity={setPeriodicity}
      />

      <div style={{ position: 'relative', minHeight: 400 }}>
        {readings.length === 0
          ? <Paragraph style={{ marginTop: 40 }}>No readings found for the selected filters.</Paragraph>
          : <Tabs defaultActiveKey="chart" items={tabsItems} />
        }
      </div>
    </div>
  )
}
